import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IUsersAndTotalCount } from 'app/library/all-users/services/all-users.api-service';
import { TenantDashboardChartId } from 'app/library/tenant-dashboard/enums/tenant-dashboard-chart-id.enum';
import { TenantDashboardChartType } from 'app/library/tenant-dashboard/enums/tenant-dashboard-chart-type.enum';
import { ITenantDashboardQuery } from 'app/library/tenant-dashboard/interfaces/tenant-dashboard.query.interface';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IEntityApiService } from 'app/projects/entity/src/lib/interfaces/entity.service.interface';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { IPaginatedResponse } from 'app/projects/subentity/src/lib/interfaces/paginated-response.interface';
import { IGlobalParticipantQuery } from 'app/projects/user/src/lib/interfaces/participant.query.interface';
import { IUserQuery } from 'app/projects/user/src/lib/interfaces/user.query.interface';
import { GlobalParticipant } from 'app/projects/user/src/lib/models/participant.model';
import { User } from 'app/projects/user/src/lib/models/user';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { peekAsyncJobIdFromResponse } from 'app/services/http-async-job-response.util';
import { ITeamMemberQuery } from '../interfaces/team-member.query.interface';
import { ITeamTreeQuery } from '../interfaces/team-tree.query.interface';
import { ITeamQuery } from '../interfaces/team.query.interface';
import { IUserTeamQuery } from '../interfaces/user-team.query.interface';
import { IAddTeamLeadersActionPayload } from '../models/add-team-leaders-action.action.payload';
import { IAddTeamMembersActionPayload } from '../models/add-team-members-action.action.payload';
import { IAddTeamWatchersActionPayload } from '../models/add-team-watchers-action.action.payload';
import { IRemoveTeamLeadersActionPayload } from '../models/remove-team-leaders-action.action.payload';
import { IRemoveTeamMembersActionPayload } from '../models/remove-team-members-action.action.payload';
import { IRemoveTeamWatchersActionPayload } from '../models/remove-team-watchers-action.action.payload';
import { TeamMember } from '../models/team-member.model';
import { TeamCommand } from '../models/team.command.model';
import { Team } from '../models/team.model';
import { UserTeam } from '../models/user-team.model';

@Injectable()
export class TeamApiService extends EntityService<ITeamQuery, TeamCommand> implements IEntityApiService<Team> {
    chartOptionsAxisTitle = {
        display: true,
        color: '#464B54',
        font: {
            family: 'Roboto',
            size: 11,
            weight: 600,
        },
    };

    chartScaleNoAxis = {
        grid: {
            drawBorder: false,
        },
    };

    chartScaleNoGrid = {
        grid: {
            display: false,
        },
    };

    chartPluginTooltip = {
        tooltip: {
            callbacks: {
                label: function (context) {
                    return context.formattedValue;
                },
            },
        },
    };

    pieChartOptions = {
        ticks: {
            precision: 0,
        },
        colors: ['#EAC196', '#B7AEDE', '#FBE78D', '#A3CAEA'],
        plugins: {
            legend: {
                display: false,
            },
        },
    };

    barChartOptions = {
        ...this.pieChartOptions,
        plugins: {
            ...this.chartPluginTooltip,
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                ...this.chartScaleNoGrid,
            },
            y: {
                ...this.chartScaleNoAxis,
                title: {
                    ...this.chartOptionsAxisTitle,
                    text: this._translateService.instant('GENERAL.DAYS'),
                    align: 'center',
                },
            },
        },
        colors: ['#C5639C'],
    };

    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) environment: IEnvironment, private _translateService: TranslateService) {
        super(http, environment.apiUrl, 'teams');
    }

    getAll(): Observable<Team[]> {
        return this._getAll().pipe(map((queries) => queries.map((query) => new Team(query))));
    }

    create(entity: Team): Observable<Team> {
        const data = new TeamCommand(entity);

        return this._create(data).pipe(map((query) => new Team(query)));
    }

    read(id: string): Observable<Team> {
        return this.http.get<ITeamQuery>(`${this.apiUrl}/${this.entities}/${id}`).pipe(map((query) => new Team(query)));
    }

    update(entity: Team): Observable<Team> {
        const data = new TeamCommand(entity);
        return this.http.patch<ITeamQuery>(`${this.apiUrl}/${this.entities}/${entity.id}`, data).pipe(
            map((query) => {
                const mappedCharts = this.getFullChartData(query);

                return new Team({ ...query, charts: mappedCharts });
            })
        );
    }

    delete(id: string): Observable<any> {
        return this._delete(id);
    }

    getTeamSubTeams(id: string, page: number = 1, limit: number = 5, query: string = ''): Observable<IPaginatedResponse<Team>> {
        let params = new HttpParams().set('page', page).set('limit', limit);
        if (query) {
            params = params.set('filter[query]', query);
        }

        return this.http.get<IPaginatedResponse<ITeamQuery>>(`${this.apiUrl}/${this.entities}/${id}/subteams`, { params }).pipe(
            map((response) => ({
                data: response.data.map((q) => {
                    const mappedCharts = this.getFullChartData(q);

                    return new Team({ ...q, charts: mappedCharts });
                }),
                pagination: response.pagination,
            }))
        );
    }

    getTeamWithSubTeamsProgress(id: string, includeSubTeamsProgress = true): Observable<Team> {
        let params = new HttpParams().set('includeSubTeamsProgress', includeSubTeamsProgress);

        return this.http.get<ITeamQuery>(`${this.apiUrl}/${this.entities}/${id}`, { params }).pipe(
            map((query) => {
                const mappedCharts = this.getFullChartData(query);

                return new Team({ ...query, charts: mappedCharts });
            })
        );
    }

    getSubTeamWithSubTeamsProgress(id: string, includeSubTeamsProgress = true): Observable<Team> {
        let params = new HttpParams().set('includeSubTeamsProgress', includeSubTeamsProgress);

        return this.http.get<ITeamQuery>(`${this.apiUrl}/${this.entities}/${id}`, { params }).pipe(
            map((query) => {
                const mappedCharts = this.getFullChartData(query);

                return new Team({ ...query, charts: mappedCharts });
            })
        );
    }

    getUserTeams(userId: string): Observable<UserTeam[]> {
        return this.http.get<IUserTeamQuery[]>(`${this.apiUrl}/users/${userId}/teams`).pipe(
            map((queries) =>
                queries.map((query) => {
                    const mappedTeams = query.teams.map((team) => {
                        const mappedCharts = this.getFullChartData(team);
                        return { ...team, charts: mappedCharts } as ITeamQuery;
                    });

                    return new UserTeam({ category: query.category, teams: mappedTeams });
                })
            )
        );
    }

    getUserTeamsV1(userId: string, role: string): Observable<Team[]> {
        return this.http.get<ITeamQuery[]>(`${this.apiUrl}/v1/users/${userId}/teams?role=${role}`).pipe(
            map((queries) =>
                queries.map((query) => {
                    return new Team(query);
                })
            )
        );
    }

    getUserProgress(userId: string): Observable<TeamMember> {
        return this.http.get<ITeamMemberQuery>(`${this.apiUrl}/users/${userId}/progress`).pipe(
            map((query) => {
                const mappedCharts = this.getFullChartData(query);

                return new TeamMember({ ...query, charts: mappedCharts });
            })
        );
    }

    getUserOtherTeams(teamId: string, userId: string): Observable<Team[]> {
        return this.http.get<ITeamQuery[]>(`${this.apiUrl}/${this.entities}/${teamId}/users/${userId}/other-teams`).pipe(
            map((queries) =>
                queries.map((query) => {
                    const mappedCharts = this.getFullChartData(query);

                    return new Team({ ...query, charts: mappedCharts });
                })
            )
        );
    }

    getTeamUsers(teamId: string, selectedTeamId: string, role: string): Observable<User[]> {
        // selectedTeamId - team where users are being added
        let params = new HttpParams().set('role', role).set('teamId', selectedTeamId);

        return this.http.get<IUserQuery[]>(`${this.apiUrl}/teams/${teamId}/users`, { params }).pipe(map((queries) => queries.map((query) => new User(query))));
    }

    getTeamsUsers(teamId = '', search = '', pageSize = 5, pageIndex = 1, role: string): Observable<IUsersAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (teamId) {
            params = params.set('teamId', teamId);
        }

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (role) {
            params = params.set('role', role);
        }

        return this.http.get<IUsersAndTotalCount>(`${this.apiUrl}/${this.entities}/users`, { params });
    }

    /* Team Members */

    getTeamMembers(teamId: string, page?: number, limit?: number): Observable<IPaginatedResponse<TeamMember>> {
        let params = new HttpParams();

        if (page != null) {
            params = params.set('page', page.toString());
        }

        if (limit != null) {
            params = params.set('limit', limit.toString());
        }

        return this.http
            .get<IPaginatedResponse<ITeamMemberQuery> | ITeamMemberQuery[]>(`${this.apiUrl}/${this.entities}/${teamId}/team-members`, { params })
            .pipe(
                map((response) => {
                    const queries = Array.isArray(response) ? response : response.data;
                    const pagination = Array.isArray(response)
                        ? { number_of_pages: 1, number_of_results: queries.length }
                        : response.pagination;

                    return {
                        data: queries.map((query) => {
                            const mappedCharts = this.getFullChartData(query);

                            return new TeamMember({ ...query, charts: mappedCharts });
                        }),
                        pagination,
                    };
                })
            );
    }

    getTeamMembersUsers(teamId: string, includeSubteams: boolean = false): Observable<GlobalParticipant[]> {
        let params = new HttpParams();

        if (includeSubteams) {
            params = params.set('includeSubteams', true.toString());
        }

        return this.http
            .get<IGlobalParticipantQuery[]>(`${this.apiUrl}/${this.entities}/${teamId}/team-members/users`, { params })
            .pipe(map((queries) => queries.map((query) => new GlobalParticipant(query))));
    }

    addUsersToTeamAsMembers({ teamIds, userIds }: IAddTeamMembersActionPayload): Observable<{ teamMembers: TeamMember[]; asyncJobId?: string }> {
        const data = {
            team_ids: teamIds.map((id) => Number(id)),
            user_ids: userIds.map((id) => Number(id)),
        };

        return this.http.put<ITeamMemberQuery[]>(`${this.apiUrl}/team-members`, data, { observe: 'response' }).pipe(
            map((resp: HttpResponse<ITeamMemberQuery[]>) => {
                const asyncJobId = peekAsyncJobIdFromResponse(resp);
                if (asyncJobId) {
                    return { teamMembers: [], asyncJobId };
                }
                const queries = resp.body || [];
                return { teamMembers: queries.map((query) => new TeamMember(query)) };
            })
        );
    }

    removeTeamMembers({ teamId, userIds }: IRemoveTeamMembersActionPayload): Observable<{ asyncJobId?: string }> {
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: {
                user_ids: userIds,
                team_id: Number(teamId),
            },
            observe: 'response' as const,
        };

        return this.http.delete<void>(`${this.apiUrl}/team-members`, options).pipe(
            map((resp: HttpResponse<void>) => {
                const asyncJobId = peekAsyncJobIdFromResponse(resp);
                return asyncJobId ? { asyncJobId } : {};
            })
        );
    }

    /* Team Leaders */

    getTeamLeaderTeams(): Observable<Team[]> {
        return this.http.get<ITeamQuery[]>(`${this.apiUrl}/${this.entities}/leader`).pipe(map((queries) => queries.map((query) => new Team(query))));
    }

    getTeamLeaders(teamId: string): Observable<User[]> {
        return this.http.get<IUserQuery[]>(`${this.apiUrl}/${this.entities}/${teamId}/team-leaders`).pipe(map((queries) => queries.map((query) => new User(query))));
    }

    addUsersToTeamAsLeaders({ teamIds, userIds, assignAsTrainingResponsible }: IAddTeamLeadersActionPayload): Observable<User[]> {
        const data = {
            team_ids: teamIds.map((id) => Number(id)),
            user_ids: userIds.map((id) => Number(id)),
            assign_as_training_responsible: !!assignAsTrainingResponsible,
        };

        return this.http.put<IUserQuery[]>(`${this.apiUrl}/team-leaders`, data).pipe(map((queries) => queries.map((query) => new User(query))));
    }

    removeTeamLeaders({ teamId, userIds }: IRemoveTeamLeadersActionPayload): Observable<void> {
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: {
                user_ids: userIds,
                team_id: Number(teamId),
            },
        };

        return this.http.delete<void>(`${this.apiUrl}/team-leaders`, options);
    }

    /* Training Responsibles */

    getTeamTrainingResponsibles(teamId: string): Observable<User[]> {
        return this.http.get<IUserQuery[]>(`${this.apiUrl}/${this.entities}/${teamId}/training-responsibles`).pipe(map((queries) => queries.map((query) => new User(query))));
    }

    addTrainingResponsibles({ teamIds, userIds }: IAddTeamLeadersActionPayload): Observable<User[]> {
        const data = {
            team_ids: teamIds.map((id) => Number(id)),
            user_ids: userIds.map((id) => Number(id)),
        };

        return this.http.put<IUserQuery[]>(`${this.apiUrl}/training-responsibles`, data).pipe(map((queries) => queries.map((query) => new User(query))));
    }

    removeTrainingResponsibles({ teamId, userIds }: IRemoveTeamLeadersActionPayload): Observable<void> {
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: {
                user_ids: userIds,
                team_id: Number(teamId),
            },
        };

        return this.http.delete<void>(`${this.apiUrl}/training-responsibles`, options);
    }

    /* Team Watchers */

    getTeamWatchers(teamId: string): Observable<User[]> {
        return this.http.get<IUserQuery[]>(`${this.apiUrl}/${this.entities}/${teamId}/team-watchers`).pipe(map((queries) => queries.map((query) => new User(query))));
    }

    addUsersToTeamAsWatchers({ teamIds, userIds }: IAddTeamWatchersActionPayload): Observable<User[]> {
        const data = {
            team_ids: teamIds.map((id) => Number(id)),
            user_ids: userIds.map((id) => Number(id)),
        };

        return this.http.put<IUserQuery[]>(`${this.apiUrl}/team-watchers`, data).pipe(map((queries) => queries.map((query) => new User(query))));
    }

    removeTeamWatchers({ teamId, userIds }: IRemoveTeamWatchersActionPayload): Observable<void> {
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: {
                user_ids: userIds,
                team_id: Number(teamId),
            },
        };

        return this.http.delete<void>(`${this.apiUrl}/team-watchers`, options);
    }

    stopWatchingTeam(teamId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${this.entities}/${teamId}/team-watchers/self`);
    }

    getFullChartData(query: ITeamMemberQuery | ITeamQuery): ITenantDashboardQuery[] {
        return query.charts.map((chart) => {
            let updatedChart = null;

            switch (chart.chart_id) {
                case TenantDashboardChartId.CourseCompletionState:
                    chart.chart_data.labels = chart.chart_data.labels.map((label) =>
                        this._translateService.instant('PAGE.MODULE_FORM.TAB.CONTENT.PARTICIPANTS_PROGRESS.STATUSES.' + label.toUpperCase())
                    );

                    updatedChart = Object.assign({}, chart, {
                        type: TenantDashboardChartType.Pie,
                        options: {
                            ...this.pieChartOptions,
                        },
                    });

                    break;
                case TenantDashboardChartId.MonthlyActiveDays:
                    chart.chart_data.labels = chart.chart_data.labels.map((label) => moment(label, 'M').format('MMM'));

                    updatedChart = Object.assign({}, chart, {
                        type: TenantDashboardChartType.Bar,
                        options: {
                            ...this.barChartOptions,
                        },
                    });

                    break;

                default:
                    break;
            }

            if (!updatedChart.aggregates) {
                updatedChart.chart_data = {
                    ...updatedChart.chart_data,
                    data_series: updatedChart.chart_data.data_series.map((dataset) => {
                        return {
                            ...dataset,
                            color: updatedChart.options.colors,
                            hoverBackgroundColor: updatedChart.options.colors,
                            hoverBorderColor: updatedChart.options.colors,
                        };
                    }),
                };
            } else {
                updatedChart.metric_color = updatedChart.options.colors[0];
            }

            return updatedChart;
        });
    }

    searchTeamsTree(search: string, pageNumber: number, pageSize: number): Observable<IPaginatedResponse<ITeamTreeQuery>> {
        let params = new HttpParams().set('page', pageNumber).set('limit', pageSize);
        if (search) {
            params = params.set('filter[query]', search);
        }

        return this.http.get<IPaginatedResponse<ITeamTreeQuery>>(`${this.apiUrl}/${this.entities}/search`, { params });
    }

    getRootTeams(): Observable<Team[]> {
        return this.http.get<ITeamQuery[]>(`${this.apiUrl}/root-teams`).pipe(map((queries) => queries.map((query) => new Team(query))));
    }

    getTeamChildren(id: string): Observable<Team[]> {
        return this.http.get<ITeamQuery[]>(`${this.apiUrl}/${this.entities}/${id}/children`).pipe(map((queries) => queries.map((query) => new Team(query))));
    }

    getTeamParent(id: string): Observable<Team> {
        return this.http.get<ITeamQuery>(`${this.apiUrl}/${this.entities}/${id}`).pipe(map((query) => new Team(query)));
    }
}
